import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { API } from "../lib/api";
import {
  Store, Share2, Package, TrendingUp, Check, ArrowRight, Upload, ShieldCheck,
  Users, Sparkles, CalendarClock, ShoppingBag, Palette, Truck, BarChart3, HeartHandshake,
} from "lucide-react";

const GOLD = "#F2B705";
const A = "https://customer-assets-0z36b82j.emergentagent.net/job_gray-spirit-gear/artifacts";
const IMG = {
  hero: `${A}/jxsky4ev_12.png`,
  whiteCrest: `${A}/qhyh3vux_10.png`,
  greyVarsity: `${A}/fuh8ayq0_14.png`,
  blackBack: `${A}/rvyw52w2_13.png`,
  whiteBack: `${A}/dxcszgp2_11.png`,
  whiteTee: `${A}/muktx8tc_16.png`,
};

const STEPS = [
  { icon: Store, n: "01", t: "Build the store", d: "We create a custom online storefront using your colors, logos, team identity, and approved merchandise." },
  { icon: Share2, n: "02", t: "Share the link", d: "You share the store link with players, parents, families, alumni, and fans." },
  { icon: Package, n: "03", t: "We handle the orders", d: "Dad Jerzeys handles ordering, payments, production, personalization, fulfillment, and customer communication." },
  { icon: TrendingUp, n: "04", t: "Your program earns", d: "A percentage of eligible merchandise sales is returned to your organization based on the partnership agreement." },
];

const GETS = [
  "Custom branded online team store", "Team-specific apparel designs", "Performance shirts & hoodies",
  "Fan gear & parent apparel", "Player name & number personalization", "Roster shirts",
  "Online payments", "Local pickup & shipping options", "Order tracking",
  "Seasonal design updates", "Sales tracking", "Fundraising giveback",
  "No paper order forms", "No collecting money by hand",
];

const AUDIENCE = [
  "School athletic programs", "Volleyball clubs", "Football teams", "Basketball teams",
  "Baseball / softball", "Cheer programs", "Dance teams", "Travel sports",
  "Booster clubs", "Youth leagues", "AAU programs", "Recreation & community orgs",
];

const MERCH = [
  "Performance tees", "Cotton tees", "Hoodies & sweatshirts", "Long sleeves",
  "Player-number apparel", "Roster gear", "Parent & fan gear", "Coach / staff merch",
  "Special-event apparel", "Limited-edition drops",
];

const DROPS = ["Season launch", "Rivalry games", "Tournaments", "Senior night", "Playoffs", "Championships", "Holidays", "Team milestones"];

const BENEFITS = [
  { icon: DollarSignIcon, t: "Additional program revenue" },
  { icon: ShoppingBag, t: "Professional team merchandise" },
  { icon: Check, t: "Easy online ordering" },
  { icon: ShieldCheck, t: "Less work for coaches & staff" },
  { icon: Users, t: "Player personalization" },
  { icon: HeartHandshake, t: "A dedicated apparel partner" },
];
function DollarSignIcon(props) { return <BarChart3 {...props} />; }

const fade = { hidden: { opacity: 0, y: 24 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06, duration: 0.5 } }) };

const Section = ({ children, className = "" }) => (
  <section className={`max-w-6xl mx-auto px-5 sm:px-6 ${className}`}>{children}</section>
);

const scrollToForm = () => document.getElementById("inquiry")?.scrollIntoView({ behavior: "smooth" });
const scrollToHow = () => document.getElementById("how")?.scrollIntoView({ behavior: "smooth" });

export default function TeamStores() {
  useEffect(() => {
    document.title = "Team Fundraising Stores | Dad Jerzeys — Custom Team Stores & Spirit Wear Fundraisers";
    const m = document.querySelector('meta[name="description"]') || (() => {
      const el = document.createElement("meta"); el.name = "description"; document.head.appendChild(el); return el;
    })();
    m.setAttribute("content", "Dad Jerzeys builds custom online team stores and spirit-wear fundraisers for schools, sports teams, clubs and booster organizations. We design the gear, build the store, and handle orders — your program raises money.");
  }, []);

  return (
    <div className="bg-[#0A0A0A] text-white">
      {/* HERO */}
      <div className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0">
          <img src={IMG.hero} alt="Custom team apparel" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/90 to-[#0A0A0A]/40" />
        </div>
        <Section className="relative py-20 sm:py-28">
          <motion.p initial="hidden" animate="show" variants={fade} className="uppercase tracking-[0.3em] text-xs font-bold mb-4" style={{ color: GOLD }}>
            Dad Jerzeys • Team Fundraising Stores
          </motion.p>
          <motion.h1 initial="hidden" animate="show" custom={1} variants={fade} className="font-display text-4xl sm:text-6xl lg:text-7xl leading-[0.95]">
            YOUR TEAM.<br />YOUR STORE.<br /><span style={{ color: GOLD }}>MONEY BACK TO YOUR PROGRAM.</span>
          </motion.h1>
          <motion.p initial="hidden" animate="show" custom={2} variants={fade} className="mt-6 max-w-xl text-gray-300 text-base sm:text-lg">
            Dad Jerzeys creates custom online team stores that make it easy for players, parents, families and fans to shop — while helping your organization raise money.
          </motion.p>
          <motion.div initial="hidden" animate="show" custom={3} variants={fade} className="mt-8 flex flex-wrap gap-3">
            <button onClick={scrollToForm} data-testid="hero-start-btn" className="inline-flex items-center gap-2 px-7 py-3.5 font-bold uppercase tracking-wider text-black rounded-sm transition-transform hover:-translate-y-0.5" style={{ background: GOLD }}>
              Start a Team Store <ArrowRight className="w-4 h-4" />
            </button>
            <button onClick={scrollToHow} data-testid="hero-how-btn" className="inline-flex items-center gap-2 px-7 py-3.5 font-bold uppercase tracking-wider border border-white/25 rounded-sm hover:border-white/60 transition-colors">
              See How It Works
            </button>
          </motion.div>
        </Section>
      </div>

      {/* VALUE PROP */}
      <Section className="py-14 text-center">
        <h2 className="font-display text-3xl sm:text-5xl">WE BUILD IT. <span style={{ color: GOLD }}>YOU SHARE IT.</span> WE HANDLE THE REST.</h2>
        <p className="mt-4 text-gray-400 max-w-2xl mx-auto">Dad Jerzeys doesn't just print shirts — we build team stores that help programs look good and raise money.</p>
      </Section>

      {/* STEPS */}
      <div id="how">
        <Section className="pb-16">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {STEPS.map((s, i) => (
              <motion.div key={s.n} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fade}
                className="border border-white/10 bg-[#141518] p-6 rounded-sm" data-testid={`step-${i}`}>
                <div className="flex items-center justify-between">
                  <s.icon className="w-8 h-8" style={{ color: GOLD }} />
                  <span className="font-display text-4xl text-white/10">{s.n}</span>
                </div>
                <h3 className="font-display text-xl mt-4">{s.t}</h3>
                <p className="text-sm text-gray-400 mt-2">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </Section>
      </div>

      {/* EVERYTHING YOUR PROGRAM NEEDS */}
      <div className="bg-[#0d0d0f] border-y border-white/10 py-16">
        <Section>
          <h2 className="font-display text-3xl sm:text-5xl mb-2">EVERYTHING YOUR PROGRAM NEEDS</h2>
          <p className="text-gray-400 mb-8">The organization promotes the store. Dad Jerzeys handles the work.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {GETS.map((g, i) => (
              <div key={i} className="flex items-center gap-3 border border-white/10 bg-[#141518] px-4 py-3 rounded-sm">
                <Check className="w-5 h-5 flex-shrink-0" style={{ color: GOLD }} />
                <span className="text-sm text-gray-200">{g}</span>
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* CASE STUDY */}
      <Section className="py-16">
        <p className="uppercase tracking-[0.3em] text-xs font-bold mb-2" style={{ color: GOLD }}>See it in action</p>
        <h2 className="font-display text-3xl sm:text-5xl mb-6">GCA VOLLEYBALL <span className="text-gray-500">×</span> DAD JERZEYS</h2>
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          <div className="grid grid-cols-3 gap-3">
            {[IMG.whiteCrest, IMG.greyVarsity, IMG.blackBack, IMG.whiteTee, IMG.whiteBack, IMG.hero].map((src, i) => (
              <div key={i} className="aspect-[4/5] overflow-hidden rounded-sm border border-white/10 bg-black">
                <img src={src} alt="GCA Volleyball gear" loading="lazy" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
          <div>
            <p className="text-gray-300">For GCA Volleyball, Dad Jerzeys created a dedicated team store where parents and fans can purchase performance apparel, hoodies, roster gear, and personalized player merchandise.</p>
            <p className="text-gray-300 mt-4">The store handles online ordering and payment while returning a percentage of eligible sales back to the volleyball program.</p>
            <div className="mt-6 flex flex-wrap gap-2">
              {["Performance tees", "Hoodies", "Player-number backs", "Roster gear", "Personalized"].map((t) => (
                <span key={t} className="text-xs uppercase tracking-wider border px-3 py-1.5 rounded-full" style={{ borderColor: GOLD, color: GOLD }}>{t}</span>
              ))}
            </div>
            <Link to="/" className="inline-flex items-center gap-2 mt-6 font-bold uppercase tracking-wider text-sm hover:underline" style={{ color: GOLD }}>
              Visit the GCA store <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </Section>

      {/* FUNDRAISING MODEL */}
      <div className="bg-[#0d0d0f] border-y border-white/10 py-16">
        <Section>
          <h2 className="font-display text-3xl sm:text-5xl mb-4">FUNDRAISING WITHOUT THE HEADACHE</h2>
          <p className="text-gray-300 max-w-3xl">Fundraising percentages and partnership structures are customized based on the program, merchandise mix, expected sales volume, and other project details.</p>
          <div className="mt-6 border-l-2 pl-5 py-1" style={{ borderColor: GOLD }}>
            <p className="text-gray-300"><span style={{ color: GOLD }} className="font-bold">Example:</span> our GCA Volleyball partnership currently includes a 15% merchandise-sales giveback to the program.</p>
          </div>
          <p className="text-gray-400 mt-6">We can discuss the structure that makes sense for your organization.</p>
        </Section>
      </div>

      {/* WHY DIFFERENT */}
      <Section className="py-16">
        <h2 className="font-display text-3xl sm:text-5xl leading-tight">NO BOXES OF UNSOLD SHIRTS.<br />NO PAPER ORDER FORMS.<br /><span style={{ color: GOLD }}>NO CHASING PAYMENTS.</span></h2>
        <div className="grid sm:grid-cols-2 gap-6 mt-8">
          <div className="border border-white/10 bg-[#141518] p-6 rounded-sm">
            <p className="uppercase tracking-wider text-xs text-gray-500 font-bold mb-3">Traditional fundraisers</p>
            <ul className="space-y-2 text-sm text-gray-400">
              {["Collect money by hand", "Track sizes on paper", "Manage order forms", "Estimate inventory", "Distribute merchandise", "Handle every question"].map((t) => (
                <li key={t} className="flex gap-2"><span className="text-red-500">✕</span> {t}</li>
              ))}
            </ul>
          </div>
          <div className="border rounded-sm p-6" style={{ borderColor: GOLD, background: "rgba(242,183,5,0.06)" }}>
            <p className="uppercase tracking-wider text-xs font-bold mb-3" style={{ color: GOLD }}>With Dad Jerzeys</p>
            <p className="text-gray-200">Customers order directly through the online store, and Dad Jerzeys manages production and fulfillment. Your staff just shares the link.</p>
          </div>
        </div>
      </Section>

      {/* WHO IT'S FOR */}
      <div className="bg-[#0d0d0f] border-y border-white/10 py-16">
        <Section>
          <h2 className="font-display text-3xl sm:text-5xl mb-8">BUILT FOR MORE THAN ONE SPORT</h2>
          <div className="flex flex-wrap gap-3">
            {AUDIENCE.map((a) => (
              <span key={a} className="border border-white/10 bg-[#141518] px-4 py-2.5 rounded-sm text-sm text-gray-200">{a}</span>
            ))}
          </div>
        </Section>
      </div>

      {/* CUSTOM MERCH + SEASONAL DROPS */}
      <Section className="py-16 grid lg:grid-cols-2 gap-10">
        <div>
          <div className="flex items-center gap-3 mb-4"><Palette className="w-7 h-7" style={{ color: GOLD }} /><h2 className="font-display text-3xl sm:text-4xl">YOUR BRAND. YOUR GEAR.</h2></div>
          <div className="grid grid-cols-2 gap-2">
            {MERCH.map((m) => (
              <div key={m} className="flex items-center gap-2 text-sm text-gray-300"><Check className="w-4 h-4" style={{ color: GOLD }} /> {m}</div>
            ))}
          </div>
          <p className="text-gray-400 text-sm mt-4">New designs can be added throughout the season to keep the store fresh and encourage repeat purchases.</p>
        </div>
        <div>
          <div className="flex items-center gap-3 mb-4"><CalendarClock className="w-7 h-7" style={{ color: GOLD }} /><h2 className="font-display text-3xl sm:text-4xl">KEEP THE STORE FRESH</h2></div>
          <p className="text-gray-400 text-sm mb-4">We can release new merchandise all season long around:</p>
          <div className="flex flex-wrap gap-2">
            {DROPS.map((d) => <span key={d} className="text-xs uppercase tracking-wider border border-white/15 px-3 py-1.5 rounded-full text-gray-300">{d}</span>)}
          </div>
        </div>
      </Section>

      {/* ORG BENEFITS */}
      <div className="bg-[#0d0d0f] border-y border-white/10 py-16">
        <Section>
          <h2 className="font-display text-3xl sm:text-5xl mb-8">WHY PROGRAMS PARTNER WITH DAD JERZEYS</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {BENEFITS.map((b, i) => (
              <div key={i} className="border border-white/10 bg-[#141518] p-6 rounded-sm flex items-start gap-4">
                <b.icon className="w-7 h-7 flex-shrink-0" style={{ color: GOLD }} />
                <span className="text-gray-200 font-semibold">{b.t}</span>
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* TESTIMONIALS + RESULTS (future-ready) */}
      <Section className="py-16 grid lg:grid-cols-2 gap-6">
        <div className="border border-dashed border-white/15 rounded-sm p-8 text-center">
          <Sparkles className="w-8 h-8 mx-auto mb-3" style={{ color: GOLD }} />
          <h3 className="font-display text-2xl">WHAT PARTNERS ARE SAYING</h3>
          <p className="text-gray-500 text-sm mt-2">Coach, parent and team testimonials will be featured here as our partnerships grow.</p>
        </div>
        <div className="border border-dashed border-white/15 rounded-sm p-8 text-center">
          <BarChart3 className="w-8 h-8 mx-auto mb-3" style={{ color: GOLD }} />
          <h3 className="font-display text-2xl">SEE WHAT YOUR TEAM CAN RAISE</h3>
          <div className="grid grid-cols-2 gap-4 mt-5 text-left">
            {[["Total store sales", "$—"], ["Returned to program", "$—"], ["Orders", "—"], ["Items sold", "—"]].map(([l, v]) => (
              <div key={l} className="bg-[#141518] border border-white/10 rounded-sm p-4">
                <p className="font-display text-3xl" style={{ color: GOLD }}>{v}</p>
                <p className="text-xs uppercase tracking-wider text-gray-500 mt-1">{l}</p>
              </div>
            ))}
          </div>
          <p className="text-gray-500 text-xs mt-4">Real fundraising results and check-presentation photos will be featured here.</p>
        </div>
      </Section>

      {/* CTA + FORM */}
      <div id="inquiry" className="border-t border-white/10 py-16" style={{ background: "linear-gradient(180deg,#0d0d0f, #0A0A0A)" }}>
        <Section>
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="font-display text-3xl sm:text-5xl">READY TO BUILD YOUR TEAM STORE?</h2>
            <p className="text-gray-400 mt-3">Tell us about your program and we'll help you build a merchandise and fundraising setup that works for your organization.</p>
          </div>
          <InquiryForm />
        </Section>
      </div>
    </div>
  );
}

const FIELDS = [
  { name: "contact_name", label: "Contact name", required: true },
  { name: "email", label: "Email", type: "email", required: true },
  { name: "phone", label: "Phone" },
  { name: "organization", label: "Organization / program name" },
  { name: "school_club", label: "School / club name" },
  { name: "sport", label: "Sport or activity" },
  { name: "location", label: "City / state" },
  { name: "participants", label: "Estimated # of participants" },
  { name: "season_start", label: "Approx. season start", type: "date" },
  { name: "season_end", label: "Approx. season end", type: "date" },
  { name: "interested_products", label: "Interested products" },
  { name: "current_provider", label: "Current merchandise provider?" },
  { name: "fundraising_pref", label: "Preferred fundraising structure (if known)" },
];

function InquiryForm() {
  const [form, setForm] = useState({ has_branding: "" });
  const [logoName, setLogoName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadFile = async (file, key) => {
    const fd = new FormData();
    fd.append("file", file);
    const r = await fetch(`${API}/team-inquiries/upload`, { method: "POST", body: fd });
    if (!r.ok) throw new Error("upload failed");
    const j = await r.json();
    set(key, j.url);
  };

  const onLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoName(file.name);
    try { await uploadFile(file, "logo_url"); toast.success("Logo attached"); }
    catch { toast.error("Logo upload failed"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.contact_name || !form.email) { toast.error("Name and email are required"); return; }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/team-inquiries`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form),
      });
      if (!r.ok) throw new Error();
      setDone(true);
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="max-w-xl mx-auto text-center border rounded-sm p-10" style={{ borderColor: GOLD, background: "rgba(242,183,5,0.06)" }} data-testid="inquiry-success">
        <Check className="w-12 h-12 mx-auto mb-4" style={{ color: GOLD }} />
        <h3 className="font-display text-2xl">Thanks!</h3>
        <p className="text-gray-300 mt-2">Dad Jerzeys will review your information and follow up about creating a team-store partnership.</p>
      </div>
    );
  }

  const inputCls = "w-full bg-black border border-white/15 rounded-sm px-3 py-2.5 text-sm focus:border-[#F2B705] outline-none";

  return (
    <form onSubmit={submit} className="max-w-3xl mx-auto border border-white/10 bg-[#141518] rounded-sm p-6 sm:p-8" data-testid="inquiry-form">
      <div className="grid sm:grid-cols-2 gap-4">
        {FIELDS.map((f) => (
          <div key={f.name} className={f.name === "interested_products" || f.name === "fundraising_pref" ? "sm:col-span-2" : ""}>
            <label className="block text-xs uppercase tracking-wider text-gray-500 mb-1">{f.label}{f.required && <span style={{ color: GOLD }}> *</span>}</label>
            <input type={f.type || "text"} value={form[f.name] || ""} onChange={(e) => set(f.name, e.target.value)}
              data-testid={`inq-${f.name}`} className={inputCls} required={f.required} />
          </div>
        ))}
        <div>
          <label className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Existing logo / branding?</label>
          <select value={form.has_branding} onChange={(e) => set("has_branding", e.target.value)} data-testid="inq-has_branding" className={inputCls}>
            <option value="">Select…</option><option value="Yes">Yes</option><option value="No">No</option>
          </select>
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Upload logo / artwork (optional)</label>
          <label className="flex items-center gap-2 bg-black border border-white/15 rounded-sm px-3 py-2.5 text-sm cursor-pointer hover:border-white/40">
            <Upload className="w-4 h-4" style={{ color: GOLD }} />
            <span className="text-gray-400 truncate">{logoName || "Choose file"}</span>
            <input type="file" accept="image/*,.pdf" onChange={onLogo} data-testid="inq-logo" className="hidden" />
          </label>
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Additional information</label>
          <textarea rows={4} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} data-testid="inq-notes" className={inputCls} />
        </div>
      </div>
      <button type="submit" disabled={submitting} data-testid="inquiry-submit"
        className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 font-bold uppercase tracking-wider text-black rounded-sm disabled:opacity-60 transition-transform hover:-translate-y-0.5" style={{ background: GOLD }}>
        {submitting ? "Sending…" : "Start Your Team Store"} <ArrowRight className="w-4 h-4" />
      </button>
    </form>
  );
}
